import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import IconButton from "@material-ui/core/IconButton";

import DeleteIcon from "@material-ui/icons/Delete";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import FilterDropdown from "designV2/components/FilterDropdown";
import { Skeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { assetUrl, readableError } from "../shopFields";
import "../superShops.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * Only the ByAdmin mutations: the form this replaces
 * (commonComponents/addProduct/AddProduct) carried four submit branches
 * behind a `type` prop, but the two ByShopAdmin ones were dead — the
 * shop admin panel has had its own product form since its redesign, and
 * every remaining call site of that file is super admin.
 * ------------------------------------------------------------------ */

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

// Create and update share one input type, and `shop` is non-null on it —
// so the update path has to send the shop as well, not just the id.
const PRODUCT_INPUT_VARS = `
  $title: String!
  $subMenu: ID
  $attributes: [AttributeItemInput]
  $reqCarTypes: [ID!]!
  $shop: ID!
  $price: Float!
  $description: String
  $photoUrl: [String]
  $stock: Int
`;

const PRODUCT_INPUT_FIELDS = `
  title: $title
  subMenu: $subMenu
  attributes: $attributes
  reqCarTypes: $reqCarTypes
  shop: $shop
  price: $price
  description: $description
  photoUrl: $photoUrl
  stock: $stock
`;

const CREATE_PRODUCT = gql`
  mutation createProductByAdmin(${PRODUCT_INPUT_VARS}) {
    createProductByAdmin(input: { ${PRODUCT_INPUT_FIELDS} }) {
      _id
      title
    }
  }
`;

const UPDATE_PRODUCT = gql`
  mutation updateProductByAdmin(${PRODUCT_INPUT_VARS} $id: ID!) {
    updateProductByAdmin(id: $id, input: { ${PRODUCT_INPUT_FIELDS} }) {
      _id
      title
    }
  }
`;

const GET_SHOP = gql`
  query getShopForProductForm($id: ID!) {
    getShopByAdmin(id: $id) {
      _id
      name
      rootCategory {
        _id
        title
      }
      shopMenu {
        _id
        subMenus {
          _id
          name
        }
      }
    }
  }
`;

const GET_ATTRIBUTE_GROUPS = gql`
  query getAttributeGroupsForProduct($rootCategory: ID) {
    getAttributeGroups(filters: { rootCategory: $rootCategory }) {
      _id
      name
      attributes {
        _id
        name
      }
    }
  }
`;

const GET_PRODUCT = gql`
  query getProductForEdit($_id: ID) {
    getProductsByAdmin(pagination: { limit: 1, skip: 0 }, filters: { _id: $_id }) {
      _id
      title
      description
      price
      stock
      photoUrl
      shopMenuName
      attributes {
        attributeGroup {
          _id
          name
        }
        att {
          _id
          name
        }
      }
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

/**
 * SuperProductForm - create or edit one of a shop's products.
 *
 * `reqCarTypes` is required by the schema but is a delivery-vehicle
 * concept that this form never collected; the old one always sent an
 * empty list, so that is preserved explicitly rather than silently.
 */
const SuperProductForm = ({ shopId, shopName, productId, subMenuId }) => {
  const isEdit = Boolean(productId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [subMenu, setSubMenu] = useState(subMenuId || "");
  const [photos, setPhotos] = useState([]);
  const [attributes, setAttributes] = useState({}); // groupId -> attributeId

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data: shopData, loading: shopLoading, error: shopError } = useQuery(GET_SHOP, {
    variables: { id: shopId },
  });
  const shop = shopData && shopData.getShopByAdmin;
  const rootCategoryId = shop && shop.rootCategory && shop.rootCategory._id;
  const isRestaurant = shop && shop.rootCategory && shop.rootCategory.title === "Restaurant";
  const words = {
    product: isRestaurant ? "Food" : "Product",
    menuItem: isRestaurant ? "Menu item" : "Category",
  };

  const { data: groupData } = useQuery(GET_ATTRIBUTE_GROUPS, {
    variables: { rootCategory: rootCategoryId },
    skip: !rootCategoryId,
    errorPolicy: "all",
  });

  const { data: productData, loading: productLoading, error: productError } = useQuery(GET_PRODUCT, {
    variables: { _id: productId },
    skip: !isEdit,
    fetchPolicy: "network-only",
  });

  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [createProduct] = useMutation(CREATE_PRODUCT);
  const [updateProduct] = useMutation(UPDATE_PRODUCT);

  const subMenus = (shop && shop.shopMenu && shop.shopMenu.subMenus) || [];
  const groups = (groupData && groupData.getAttributeGroups) || [];

  const existing = productData && productData.getProductsByAdmin[0];
  useEffect(() => {
    if (!existing) return;
    setTitle(existing.title || "");
    setDescription(existing.description || "");
    setPrice(existing.price === null || existing.price === undefined ? "" : String(existing.price));
    setStock(existing.stock === null || existing.stock === undefined ? "" : String(existing.stock));
    setPhotos(existing.photoUrl || []);
    const next = {};
    (existing.attributes || []).forEach((item) => {
      if (item.attributeGroup && item.att) next[item.attributeGroup._id] = item.att._id;
    });
    setAttributes(next);
  }, [existing]);

  // The edit query returns the sub-menu by name, not id, so it is matched
  // back to the shop's own menu once both have arrived.
  useEffect(() => {
    if (!existing || !existing.shopMenuName || !subMenus.length || subMenu) return;
    const match = subMenus.find((s) => s.name === existing.shopMenuName);
    if (match) setSubMenu(match._id);
  }, [existing, subMenus, subMenu]);

  const subMenuOptions = useMemo(
    () => subMenus.map((s) => ({ value: s._id, label: s.name })),
    [subMenus]
  );
  const subMenuLabel = (subMenuOptions.find((o) => o.value === subMenu) || {}).label;

  /* ---------------- photos ---------------- */

  const onPickPhoto = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setUploading(true);
    setFormError("");
    uploadFile({ variables: { file, folderName: "products" } })
      .then((res) => {
        setUploading(false);
        const url = res.data.uploadFile.url;
        setPhotos((current) => current.concat(url));
      })
      .catch((err) => {
        setUploading(false);
        setFormError(readableError(err));
      });
    // Allow picking the same file again after removing it.
    event.target.value = "";
  };

  const removePhoto = (url) => setPhotos((current) => current.filter((item) => item !== url));

  /* ---------------- submit ---------------- */

  const validate = () => {
    const next = {};
    if (!title.trim()) next.title = "A name is required.";
    const priceValue = parseFloat(price);
    if (price === "" || Number.isNaN(priceValue) || priceValue < 0) {
      next.price = "Enter a price of zero or more.";
    }
    if (stock !== "" && (Number.isNaN(parseInt(stock, 10)) || parseInt(stock, 10) < 0)) {
      next.stock = "Stock must be zero or more.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving || uploading) return;
    setFormError("");
    if (!validate()) return;

    const attributeList = Object.keys(attributes)
      .filter((groupId) => attributes[groupId])
      .map((groupId) => ({ attributeGroup: groupId, att: attributes[groupId] }));

    const common = {
      title: title.trim(),
      subMenu: subMenu || undefined,
      attributes: attributeList,
      reqCarTypes: [],
      price: parseFloat(price),
      description: description.trim() || undefined,
      photoUrl: photos,
      stock: stock === "" ? undefined : parseInt(stock, 10),
      shop: shopId,
    };

    setSaving(true);
    const run = isEdit
      ? updateProduct({ variables: { ...common, id: productId } })
      : createProduct({ variables: common });

    run
      .then(() => {
        window.location.href = `${endPoint}/superAdmin/${shopName}/${shopId}/products`;
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  /* ---------------- render ---------------- */

  if (shopLoading || (isEdit && productLoading)) {
    return (
      <div className="page superShopsPage">
        <main className="main">
          <PageHeader title={isEdit ? "Edit item" : "New item"} />
          <Skeleton height={320} />
        </main>
      </div>
    );
  }

  if (shopError || (isEdit && productError)) return <ErrorPage />;

  return (
    <div className="page superShopsPage">
      <main className="main">
        <PageHeader
          title={isEdit ? `Edit ${words.product.toLowerCase()}` : `New ${words.product.toLowerCase()}`}
          description={shop ? `For ${shop.name}.` : ""}
        />

        <form onSubmit={onSubmit}>
          <Panel title="Details">
            <div className="superShopForm" style={{ width: "100%" }}>
              {formError && (
                <div className="dsFormMessage error" role="alert">
                  {formError}
                </div>
              )}

              <Field label="Name" htmlFor="title" error={errors.title} required>
                <input
                  id="title"
                  className="dsInput"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>

              <Field label="Description" htmlFor="description" optional>
                <textarea
                  id="description"
                  className="dsInput"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <div className="superShopFormRow">
                <Field label="Price" htmlFor="price" error={errors.price} required>
                  <input
                    id="price"
                    className="dsInput"
                    type="number"
                    min="0"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </Field>

                <Field
                  label="Stock"
                  htmlFor="stock"
                  error={errors.stock}
                  optional
                  hint="Leave blank if you do not track stock for this item."
                >
                  <input
                    id="stock"
                    className="dsInput"
                    type="number"
                    min="0"
                    value={stock}
                    onChange={(e) => setStock(e.target.value)}
                  />
                </Field>
              </div>

              <Field label={words.menuItem} hint="Where customers will find this item.">
                <FilterDropdown
                  label={subMenuLabel || `Choose a ${words.menuItem.toLowerCase()}`}
                  options={
                    subMenuOptions.length
                      ? [{ value: "", label: "Not grouped" }, ...subMenuOptions]
                      : [{ value: "", label: "This shop has no groups yet" }]
                  }
                  value={subMenu}
                  onChange={(e) => setSubMenu(e.target.value)}
                />
              </Field>
            </div>
          </Panel>

          <Panel title="Photos" subtitle="The first one is used as the thumbnail.">
            <div className="superShopImages">
              {photos.map((url) => (
                <span className="superShopImageButton" key={url} style={{ position: "relative" }}>
                  <img src={assetUrl(url)} alt="" />
                  <IconButton
                    size="small"
                    className="rowActionButton"
                    onClick={() => removePhoto(url)}
                    title="Remove photo"
                    style={{ position: "absolute", top: 2, right: 2, background: "rgba(255,255,255,0.9)" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              ))}
              <label className="superShopImageButton" style={{ display: "grid", placeItems: "center", width: 140, height: 90, cursor: "pointer" }}>
                <input type="file" accept="image/*" hidden onChange={onPickPhoto} />
                <AddPhotoAlternateIcon />
              </label>
            </div>
            {uploading && (
              <div className="dsFormMessage info" role="status">
                Uploading photo…
              </div>
            )}
          </Panel>

          {groups.length > 0 && (
            <Panel title="Options" subtitle="Pick one value per group where it applies.">
              <div className="superShopFormRow">
                {groups.map((group) => {
                  const options = (group.attributes || []).map((a) => ({
                    value: a._id,
                    label: a.name,
                  }));
                  const selected = options.find((o) => o.value === attributes[group._id]);
                  return (
                    <Field label={group.name} key={group._id}>
                      <FilterDropdown
                        label={selected ? selected.label : "Not set"}
                        options={[{ value: "", label: "Not set" }, ...options]}
                        value={attributes[group._id] || ""}
                        onChange={(e) =>
                          setAttributes((current) => ({ ...current, [group._id]: e.target.value }))
                        }
                      />
                    </Field>
                  );
                })}
              </div>
            </Panel>
          )}

          <div className="superShopFooter">
            <Button
              variant="secondary"
              onClick={() =>
                (window.location.href = `${endPoint}/superAdmin/${shopName}/${shopId}/products`)
              }
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving} disabled={uploading}>
              {isEdit ? "Save changes" : `Create ${words.product.toLowerCase()}`}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SuperProductForm;
